import { useState, useRef, useEffect, useCallback } from "react";
import { Sparkles, Send, FileDown, Save, Briefcase, Bot, Plus, MessageSquare } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { buildResumePdf, RESUME_TEMPLATES, resumeSections } from "../lib/resumePdf.mjs";
import { toast } from "sonner";
import { api, errMsg, useFetch } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, btn, Loading, ErrorBox, TextField, TextArea } from "../components/kit";
import { JobCard, GovCard } from "../components/cards";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const SUGGESTIONS = [
  "How do I start a career in data analytics?",
  "Give me tips to improve my resume",
  "What kinds of government schemes might help me?",
  "How do I prepare for my first interview?",
];

const markdownComponents = {
  h1: ({ children }) => <h3 className="mb-2 mt-4 text-lg font-bold first:mt-0">{children}</h3>,
  h2: ({ children }) => <h3 className="mb-2 mt-4 text-base font-bold first:mt-0">{children}</h3>,
  h3: ({ children }) => <h3 className="mb-2 mt-4 text-base font-bold first:mt-0">{children}</h3>,
  p: ({ children }) => <p className="my-2 whitespace-pre-line leading-relaxed first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="my-2 list-disc space-y-1 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="my-2 list-decimal space-y-1 pl-5">{children}</ol>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  a: ({ children }) => <span>{children}</span>,
};

function AdvisorMessage({ message, index }) {
  const assistant = message.role === "assistant";
  return <div className={`max-w-[90%] break-words rounded-xl px-4 py-3 text-sm ${assistant ? "bg-mist text-ink" : "ml-auto bg-blue text-white"}`} data-testid={`advisor-msg-${message.role}-${index}`}>
    {assistant ? <ReactMarkdown skipHtml allowedElements={["h1", "h2", "h3", "h4", "p", "ul", "ol", "li", "strong", "em", "br", "a"]} unwrapDisallowed components={markdownComponents}>{message.text.replace(/^[ \t]*•[ \t]+/gm, "- ")}</ReactMarkdown> : <p className="whitespace-pre-line">{message.text}</p>}
    {message.source === "fallback" && <p className="mt-2 text-xs text-sub" data-testid={`advisor-offline-${index}`}>Offline guidance — AI is currently unavailable</p>}
  </div>;
}

function Advisor() {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(false);
  const [sid, setSid] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [more, setMore] = useState(false);
  const [historyBusy, setHistoryBusy] = useState(true);
  const [historyError, setHistoryError] = useState("");
  const logRef = useRef(null);
  const loadSessions = useCallback(async (offset = 0) => {
    setHistoryBusy(true); setHistoryError("");
    try {
      const { data } = await api.get(`/ai/conversations?offset=${offset}`);
      setSessions((old) => offset ? [...old, ...data.items] : data.items);
      setMore(data.has_more);
    } catch (e) { setHistoryError(errMsg(e)); }
    finally { setHistoryBusy(false); }
  }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [msgs, busy]);
  const openChat = async (sessionId) => {
    if (busy || opening) return;
    setOpening(true);
    try {
      const { data } = await api.get(`/ai/advisor/${sessionId}`);
      setMsgs(data); setSid(sessionId); setInput("");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setOpening(false); }
  };
  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy || opening) return;
    setMsgs((m) => [...m, { role: "user", text }]);
    setInput(""); setBusy(true);
    try {
      const { data } = await api.post("/ai/advisor", { message: text, session_id: sid });
      setSid(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", text: data.reply, source: data.source }]);
      await loadSessions();
    } catch (e2) {
      toast.error(errMsg(e2));
      setMsgs((m) => m.slice(0, -1)); setInput(text);
    } finally { setBusy(false); }
  };
  return <div className="grid items-start gap-4 lg:grid-cols-[250px_minmax(0,1fr)]" data-testid="ai-advisor">
    <aside className="card space-y-3 p-4" aria-label="Saved advisor conversations" data-testid="advisor-chat-history">
      <div className="flex items-center gap-2 font-bold text-navy"><MessageSquare className="h-4 w-4" aria-hidden="true" />Your conversations</div>
      <p className="text-xs text-sub" data-testid="advisor-history-notice">Saved automatically. Only you can access your chats.</p>
      <button className={btn("secondary", "sm")} disabled={busy || opening} onClick={() => { setMsgs([]); setSid(null); setInput(""); }} data-testid="advisor-new-chat"><Plus className="h-4 w-4" aria-hidden="true" />New chat</button>
      {historyError && <div role="alert" data-testid="advisor-history-error"><p className="text-sm text-err">{historyError}</p><button onClick={() => loadSessions()} className={btn("ghost", "sm")} data-testid="advisor-history-retry">Retry history</button></div>}
      {historyBusy && <p role="status" className="text-sm text-sub" data-testid="advisor-history-loading">Loading conversations…</p>}
      {!historyBusy && !historyError && !sessions.length && <p className="text-sm text-sub" data-testid="advisor-history-empty">Your first message starts a saved conversation.</p>}
      <ul className="max-h-64 space-y-1 overflow-y-auto lg:max-h-[420px]">{sessions.map((s) => <li key={s.session_id}>
        <button className={`min-h-[44px] w-full rounded-lg border p-3 text-left transition-colors ${sid === s.session_id ? "border-blue bg-mist" : "border-transparent hover:bg-surface"}`} disabled={busy || opening} aria-pressed={sid === s.session_id} onClick={() => openChat(s.session_id)} data-testid={`advisor-chat-${s.session_id}`}>
          <span className="block truncate text-sm font-semibold">{s.title}</span><span className="mt-1 block text-xs text-sub">{new Date(s.updated_at).toLocaleDateString()} · {s.message_count} messages</span>
        </button>
      </li>)}</ul>
      {more && <button className={btn("ghost", "sm")} disabled={historyBusy || busy} onClick={() => loadSessions(sessions.length)} data-testid="advisor-history-more">Load older chats</button>}
    </aside>
    <div className="card min-w-0 p-5">
      <h2 className="mb-4 text-lg font-bold" data-testid="advisor-conversation-title">{sessions.find((s) => s.session_id === sid)?.title || "New conversation"}</h2>
      <div ref={logRef} className="min-h-[320px] max-h-[460px] space-y-3 overflow-y-auto pr-1" role="log" aria-live="polite" aria-busy={busy || opening} aria-label="Advisor conversation" data-testid="advisor-conversation-log">
        {opening ? <Loading text="Opening conversation…" /> : <>
          {!msgs.length && <div className="text-sub">
            <p className="flex items-center gap-2 font-semibold text-navy"><Bot className="h-5 w-5" aria-hidden="true" />What would you like to work on?</p>
            <div className="mt-4 flex flex-wrap gap-2">{SUGGESTIONS.map((s, i) => <button key={s} type="button" className={btn("secondary", "sm")} onClick={() => setInput(s)} data-testid={`advisor-suggestion-${i}`}>{s}</button>)}</div>
          </div>}
          {msgs.map((m, i) => <AdvisorMessage key={m.id || i} message={m} index={i} />)}
        </>}
        {busy && <div className="rounded-xl bg-mist px-4 py-3 text-sm" data-testid="advisor-typing">Thinking…</div>}
      </div>
      <form onSubmit={send} className="mt-4 flex items-end gap-2">
        <div className="min-w-0 flex-1"><TextField id="advisor-input" label="Your message" required maxLength={2000} disabled={busy || opening} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Type your question…" /></div>
        <button className={btn("primary")} disabled={busy || opening || !input.trim()} data-testid="advisor-send-btn"><Send className="h-4 w-4" aria-hidden="true" />Send</button>
      </form>
      <p className="mt-3 text-xs text-sub" data-testid="advisor-disclaimer">
        General guidance, not professional advice. Browse OpenPath for listings; sample opportunities are
        labelled. AI responses use your configured provider and may consume its balance.
      </p>
    </div>
  </div>;
}

function MatchReasons({ reasons = [], testid, general = false }) {
  return <div className="w-full rounded-r-lg border-l-4 border-blue bg-mist p-3 text-sm text-navy" data-testid={testid}>
    <p className="font-semibold">{general ? "Explore this opportunity" : "Why this matches"}</p>
    <ul className="mt-1 list-disc space-y-1 pl-4">{reasons.map((reason, i) => <li key={i}>{reason}</li>)}</ul>
  </div>;
}

function Matches() {
  const { data, loading, error, reload } = useFetch("/ai/matches");
  if (loading) return <Loading />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  const { jobs = [], internships = [] } = data;
  const Section = ({ title, items, testid }) => (
    <div data-testid={testid}>
      <h3 className="flex items-center gap-2 text-lg font-bold"><Briefcase className="h-5 w-5" aria-hidden="true" />{title}</h3>
      {items.length
        ? <ul className="mt-3 grid gap-3 md:grid-cols-2">{items.map((j) => <li key={j.id}><JobCard job={j} extra={<MatchReasons reasons={j.match_reasons} testid={`${testid}-reasons-${j.id}`} />} /></li>)}</ul>
        : <p className="mt-2 rounded-lg bg-surface p-4 text-sub">No matches yet. Add skills and a location to your profile to improve matching.</p>}
    </div>
  );
  return (
    <div className="space-y-6" data-testid="ai-matches">
      <p className="text-sub">
        Matched from your profile skills, location and preferences against live OpenPath listings —
        rule-based, with no invented jobs.
      </p>
      <Section title="Job matches" items={jobs} testid="match-jobs" />
      <Section title="Internship matches" items={internships} testid="match-internships" />
    </div>
  );
}

function Schemes() {
  const { data, loading, error, reload } = useFetch("/ai/schemes");
  if (loading) return <Loading />;
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  return (
    <div className="space-y-4" data-testid="ai-schemes">
      <p className="text-sub">Government jobs, schemes and guides matched to your profile. Entries marked “SAMPLE” are not real opportunities.</p>
      {data.length
        ? <ul className="grid gap-3 md:grid-cols-2">{data.map((g) => <li key={g.id}><GovCard g={g} extra={<MatchReasons reasons={g.match_reasons} general={g.match_type === "general"} testid={`scheme-reasons-${g.id}`} />} /></li>)}</ul>
        : <p className="rounded-lg bg-surface p-4 text-sub">No schemes matched yet. Add interests and a location to your profile.</p>}
    </div>
  );
}

function ResumePreview({ fields, template }) {
  const compact = template.id === "compact";
  return <aside className="min-w-0 rounded-xl border border-line bg-surface p-4 lg:sticky lg:top-6" data-testid="resume-preview-panel">
    <p className="mb-3 text-sm font-semibold text-sub" data-testid="resume-preview-label">{template.name} · Live style preview</p>
    <div className={`max-h-[620px] overflow-y-auto break-words border border-line bg-white p-6 shadow-sm ${template.id === "modern" ? "border-t-8 border-t-navy" : ""}`} style={{ fontFamily: template.id === "classic" ? "Georgia, serif" : "Helvetica, sans-serif", color: template.color }} data-testid="resume-preview">
      <p className={`font-bold ${compact ? "text-xl" : "text-2xl"}`} data-testid="resume-preview-name">{fields.name || "Your Name"}</p>
      {fields.headline && <p className="mt-1 text-sm">{fields.headline}</p>}
      <p className="mt-2 text-xs">{[fields.email, fields.location].filter(Boolean).join(" | ")}</p>
      <hr className="my-4 border-line" />
      {resumeSections(fields).filter(([, body]) => body.trim()).map(([title, body]) => <section key={title} className={compact ? "mb-3" : "mb-5"}>
        <h3 className="mb-1 text-xs font-bold uppercase tracking-wider">{title}</h3>
        <p className={`whitespace-pre-wrap ${compact ? "text-xs leading-5" : "text-sm leading-6"}`}>{body}</p>
      </section>)}
    </div>
    <p className="mt-3 text-xs text-sub" data-testid="resume-preview-note">Style preview; PDF page breaks may differ. Long resumes continue onto additional pages.</p>
  </aside>;
}

function Resume() {
  const { user } = useAuth();
  const p = user.profile || {};
  const [templateId, setTemplateId] = useState("classic");
  const [saving, setSaving] = useState(false);
  const template = RESUME_TEMPLATES.find((t) => t.id === templateId);
  const [f, setF] = useState({
    name: user.name || "", email: user.email || "", headline: p.headline || "", location: p.location || "",
    skills: (p.skills || []).join(", "), experience: p.experience || "", education: p.education || "", bio: p.bio || "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/me/profile", { name: f.name, profile: { ...p, headline: f.headline, location: f.location, skills: f.skills.split(",").map((s) => s.trim()).filter(Boolean), experience: f.experience, education: f.education, bio: f.bio } });
      toast.success("Saved to your profile");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const download = () => {
    try {
      const doc = buildResumePdf(f, templateId);
      doc.save(`${(f.name || "resume").replace(/[^a-z0-9_-]+/gi, "_")}_${templateId}_resume.pdf`);
      toast.success(`${template.name} resume PDF downloaded`);
    } catch { toast.error("We couldn't create the PDF. Please try again."); }
  };
  return <div className="space-y-6" data-testid="resume-builder">
    <p className="text-sub" data-testid="resume-intro">One resume, three styles. Choose a layout and preview your changes before downloading. PDF creation stays in your browser.</p>
    <fieldset>
      <legend className="mb-3 font-bold text-navy">Choose your resume style</legend>
      <div className="grid gap-3 sm:grid-cols-3">{RESUME_TEMPLATES.map((t) => <label key={t.id} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${templateId === t.id ? "border-blue bg-mist ring-1 ring-blue" : "border-line bg-white hover:bg-surface"}`}>
        <input type="radio" name="resume-template" value={t.id} checked={templateId === t.id} onChange={() => setTemplateId(t.id)} className="mt-1 h-4 w-4 accent-blue" data-testid={`resume-template-${t.id}`} />
        <span><span className={`block text-lg font-bold ${t.id === "classic" ? "font-serif" : ""}`}>{t.name}</span><span className="mt-1 block text-sm text-sub">{t.description}</span></span>
      </label>)}</div>
    </fieldset>
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
      <div className="card min-w-0 space-y-4 p-5">
        <div className="grid gap-4 md:grid-cols-2">
          <TextField id="r-name" label="Full name" value={f.name} onChange={set("name")} />
          <TextField id="r-email" label="Email" value={f.email} onChange={set("email")} hint="For this PDF only; account email is unchanged." />
          <TextField id="r-headline" label="Headline" value={f.headline} onChange={set("headline")} />
          <TextField id="r-location" label="Location" value={f.location} onChange={set("location")} />
        </div>
        <TextField id="r-skills" label="Skills (comma separated)" value={f.skills} onChange={set("skills")} />
        <TextArea id="r-bio" label="Professional summary" rows={3} value={f.bio} onChange={set("bio")} />
        <TextArea id="r-exp" label="Experience" rows={4} value={f.experience} onChange={set("experience")} />
        <TextArea id="r-edu" label="Education" rows={3} value={f.education} onChange={set("education")} />
        <div className="flex flex-wrap gap-2">
          <button className={btn("primary")} onClick={download} data-testid="resume-download-btn"><FileDown className="h-4 w-4" aria-hidden="true" />Download {template.name} PDF</button>
          <button className={btn("secondary")} disabled={saving} onClick={saveProfile} data-testid="resume-save-btn"><Save className="h-4 w-4" aria-hidden="true" />{saving ? "Saving…" : "Save to profile"}</button>
        </div>
      </div>
      <ResumePreview fields={f} template={template} />
    </div>
  </div>;
}

export default function CareerAI() {
  const [tab, setTab] = useState("advisor");
  return (
    <>
      <PageHeader eyebrow="AI tools" title="AI Career Assistant" description="Personalised guidance, job and internship matches, government schemes, and a resume builder." testid="career-ai-header">
        <Sparkles className="h-8 w-8 text-blue" aria-hidden="true" />
      </PageHeader>
      <div className="container-x py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex h-auto flex-wrap gap-1" data-testid="career-ai-tabs">
            <TabsTrigger value="advisor" data-testid="tab-advisor">AI Advisor</TabsTrigger>
            <TabsTrigger value="matches" data-testid="tab-matches">Job & Internship Matches</TabsTrigger>
            <TabsTrigger value="schemes" data-testid="tab-schemes">Government Schemes</TabsTrigger>
            <TabsTrigger value="resume" data-testid="tab-resume">Resume Builder</TabsTrigger>
          </TabsList>
          <TabsContent value="advisor" forceMount className="mt-6 data-[state=inactive]:hidden"><Advisor /></TabsContent>
          <TabsContent value="matches" className="mt-6"><Matches /></TabsContent>
          <TabsContent value="schemes" className="mt-6"><Schemes /></TabsContent>
          <TabsContent value="resume" className="mt-6"><Resume /></TabsContent>
        </Tabs>
      </div>
    </>
  );
}
